
import { useState, useCallback } from "react";
import { debounce } from "lodash";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { User } from "@supabase/supabase-js";
import { VALID_CONTENT_TYPES } from "@/utils/contentFormUtils";

/**
 * Hook to handle auto-saving content changes
 */
export const useContentAutoSave = (contentItemId: string, user: User | null, onUpdate: () => void) => {
  const [isSaving, setIsSaving] = useState(false);

  // Create debounced save function for auto-saving
  const debouncedSave = debounce(async (updates: any) => {
    if (!contentItemId) return;
    
    setIsSaving(true);
    console.log(`[DEBUG] Saving updates for content ID: ${contentItemId}`, updates);
    
    try {
      // Make a copy of updates to avoid modifying the original object
      const updatesCopy = { ...updates };
      
      // Extract collection_id if it exists in the updates
      const collection_id = updatesCopy.collection_id;
      delete updatesCopy.collection_id;
      
      console.log(`[DEBUG] Content updates after collection_id extraction:`, updatesCopy);
      
      let hasUpdatedContent = false;
      
      // Only update content item if there are properties to update
      if (Object.keys(updatesCopy).length > 0) {
        // Always ensure content_type is lowercase if present
        if (updatesCopy.content_type !== undefined) {
          const originalType = updatesCopy.content_type;
          updatesCopy.content_type = originalType.toLowerCase();
          console.log(`[DEBUG] Normalized content_type to lowercase: "${updatesCopy.content_type}"`);
          
          // Validate content type against allowed application types
          if (!VALID_CONTENT_TYPES.includes(updatesCopy.content_type)) {
            console.error(`[DEBUG] The content type "${updatesCopy.content_type}" is not allowed.`);
            toast.error(`Cannot set content type to "${updatesCopy.content_type}". Only these types are supported: ${VALID_CONTENT_TYPES.join(", ")}`);
            setIsSaving(false);
            return;
          }
        }
        
        // Log the exact update operation we're about to perform
        console.log(`[DEBUG] Executing Supabase update for content_items with ID: ${contentItemId}`, updatesCopy);
        
        // Add a select() call to return the updated data, which ensures the update completes fully
        const { data, error } = await supabase
          .from("content_items")
          .update(updatesCopy)
          .eq("id", contentItemId)
          .select();
  
        if (error) {
          console.error("[DEBUG] Error updating content:", error);
          throw error;
        }
        
        console.log(`[DEBUG] Supabase update response:`, data);
        hasUpdatedContent = true;
        
        // Verify that the content type was updated correctly
        if (updatesCopy.content_type && data && data[0]) {
          console.log(`[DEBUG] Verified content_type update: ${data[0].content_type} (expected: ${updatesCopy.content_type})`);
          if (data[0].content_type !== updatesCopy.content_type) {
            console.error(`[DEBUG] Content type mismatch: ${data[0].content_type} vs ${updatesCopy.content_type}`);
          }
        }
      }

      // Update collection association if it was included in the updates
      let hasUpdatedCollection = false;
      if (collection_id !== undefined) {
        console.log(`[DEBUG] Updating collection association for content ID: ${contentItemId} to collection: ${collection_id}`);
        
        try {
          // First delete existing association
          const { error: deleteError } = await supabase
            .from("content_collections")
            .delete()
            .eq("content_id", contentItemId);

          if (deleteError && deleteError.code !== 'PGRST116') { // Ignore if no rows deleted
            console.error("[DEBUG] Error deleting collection association:", deleteError);
            throw deleteError;
          }

          // Then insert new association if not empty
          if (collection_id && collection_id.trim() !== "") {
            const { error: insertError } = await supabase
              .from("content_collections")
              .insert({
                content_id: contentItemId,
                collection_id: collection_id,
              });

            if (insertError) {
              console.error("[DEBUG] Error inserting collection association:", insertError);
              throw insertError;
            }
          }
          
          hasUpdatedCollection = true;
        } catch (collectionError) {
          console.error("[DEBUG] Error updating collection:", collectionError);
          // Even if collection update fails, we'll still consider the overall operation successful
          // if we at least updated the content successfully
        }
      }
      
      if (hasUpdatedContent || hasUpdatedCollection) {
        console.log(`[DEBUG] Content updated successfully for ID: ${contentItemId}`);
        toast.success("Changes saved", { duration: 2000 });
        
        // Important: Call onUpdate to refresh data and ensure UI state is in sync with database
        onUpdate(); 
        
        return true; // Return true to indicate the save was successful
      } else {
        console.log(`[DEBUG] No changes to save for ID: ${contentItemId}`);
        return true; // No changes to save, but technically successful
      }
    } catch (error: any) {
      console.error("[DEBUG] Error updating content:", error);
      toast.error(error.message || "Failed to save changes");
      return false; // Return false to indicate the save failed
    } finally {
      setIsSaving(false);
    }
  }, 1000);

  return { isSaving, debouncedSave };
};
